// src/pages/LMS/LMSEmbedPages.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Grid, Card, CardContent, CardActions,
  Chip, IconButton, Menu, MenuItem, TextField, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl,
  InputLabel, Select, Alert, Pagination, CircularProgress, Stack,
  Tooltip, Divider, ListItemIcon, ListItemText, Switch, FormControlLabel,
  Tabs, Tab, Accordion, AccordionSummary, AccordionDetails,
  Avatar, Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Visibility as ViewIcon,
  Publish as PublishIcon,
  Pause as PauseIcon,
  Code as CodeIcon,
  Analytics as AnalyticsIcon,
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  ViewModule as GridIcon,
  ViewList as ListIcon,
  Fingerprint as IdIcon,
  Palette as PaletteIcon,
  FormatSize as TypographyIcon,
  AspectRatio as DimensionIcon,
  ViewQuilt as LayoutIcon,
  TextFields as HeaderIcon,
  Image as ImageIcon,
  TouchApp as ButtonIcon,
  Security as SecurityIcon,
  TrendingUp as StatsIcon,
  FilterList as FilterIcon,
  School as SchoolIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosInstance';
import { hasPermission } from '../../utils/permissionUtils';

// Color Picker Component
const ColorPicker = ({ label, value, onChange, helperText }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <input
        type="color"
        value={value || '#000000'}
        onChange={(e) => onChange(e.target.value)}
        style={{ 
          width: 40, 
          height: 40, 
          border: '1px solid #ddd', 
          borderRadius: 4, 
          cursor: 'pointer',
          padding: 0
        }}
      />
      <TextField
        size="small"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        sx={{ flex: 1 }}
        placeholder="#000000"
      />
    </Box>
    {helperText && (
      <Typography variant="caption" color="text.secondary">{helperText}</Typography>
    )}
  </Box>
);

// Tab Panel Component
const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
  </div>
);

const LMSEmbedPages = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  // State
  const [embedPages, setEmbedPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Permissions
  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, embed: null });
  const [codeDialog, setCodeDialog] = useState({ open: false, embed: null });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedEmbed, setSelectedEmbed] = useState(null);

  // Editor states
  const [activeTab, setActiveTab] = useState(0);
  const [embedForm, setEmbedForm] = useState(getDefaultEmbedForm());
  const [copiedCode, setCopiedCode] = useState('');
  const [saving, setSaving] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Get default form values
  function getDefaultEmbedForm() {
    return {
      name: '',
      description: '',
      status: 'draft',
      layout: {
        type: 'grid',
        columns: 3,
        card_style: 'standard',
        gap: '24px',
      },
      dimensions: {
        width: '100%',
        max_width: '1200px',
        min_height: '400px',
        padding: '24px',
        border_radius: '12px',
        card_border_radius: '8px',
      },
      colors: {
        primary: '#1976d2',
        secondary: '#dc004e',
        background: '#f5f5f5',
        card_background: '#ffffff',
        header_background: '#ffffff',
        text: '#333333',
        heading: '#1a1a1a',
        subtext: '#666666',
        button: '#1976d2',
        button_text: '#ffffff',
        button_hover: '#1565c0',
        card_border: '#e0e0e0',
        card_shadow: '0 2px 8px rgba(0,0,0,0.1)',
        difficulty_beginner: '#4caf50',
        difficulty_intermediate: '#ff9800',
        difficulty_advanced: '#f44336',
        category_chip: '#e3f2fd',
        category_chip_text: '#1976d2',
      },
      typography: {
        font_family: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        header_title_size: '28px',
        header_subtitle_size: '16px',
        card_title_size: '18px',
        card_description_size: '14px',
        button_font_size: '14px',
        heading_font_weight: '700',
        card_title_font_weight: '600',
      },
      header: {
        show: true,
        title: 'Our Courses',
        subtitle: 'Explore our comprehensive training programs',
        alignment: 'center',
        show_logo: false,
        logo_url: '',
        logo_height: '40px',
      },
      filters: {
        show_search: true,
        search_placeholder: 'Search courses...',
        show_category_filter: true,
        show_difficulty_filter: true,
        show_sort_options: true,
      },
      card_display: {
        show_thumbnail: true,
        thumbnail_height: '180px',
        default_thumbnail_url: '',
        show_title: true,
        show_description: true,
        description_lines: 2,
        show_duration: true,
        show_difficulty: true,
        show_category: true,
        show_module_count: true,
        show_enrolled_count: false,
      },
      buttons: {
        show_enroll: true,
        enroll_text: 'Enroll Now',
        enroll_style: 'filled',
        enroll_full_width: true,
        button_border_radius: '6px',
        show_details: false,
        details_text: 'View Details',
      },
      redirect: {
        enroll_url: '',
        enroll_template: '',
        open_new_tab: true,
      },
      pagination: {
        enabled: true,
        per_page: 9,
        style: 'numbered',
      },
      course_filter: null,
      max_courses: 12,
      sort_by: 'created_at',
      sort_order: 'desc',
      empty_state: {
        title: 'No courses available',
        message: 'Check back later for new courses.',
      },
      footer: {
        show: false,
        text: '',
        show_powered_by: true,
      },
      custom_code: {
        css: '',
      },
      security: {
        allowed_domains: [],
        require_domain_verification: false,
      },
    };
  }

  // Check permissions
  useEffect(() => {
    const checkPerms = async () => {
      try {
        const viewPerm = await hasPermission('lms', 'view');
        const modifyPerm = await hasPermission('lms', 'add|edit');
        const deletePerm = await hasPermission('lms', 'delete');
        setCanView(viewPerm);
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error('Error checking permissions:', error);
      } finally {
        setCheckingPermissions(false);
      }
    };
    checkPerms();
  }, []);

  // Load embed pages
  useEffect(() => {
    if (canView && chatbotId) {
      loadEmbedPages();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, chatbotId, page, statusFilter, searchTerm]);

  const loadEmbedPages = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: 12,
        chatbot_id: chatbotId,
      };

      if (statusFilter) params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;

      const response = await axiosInstance.get('/api/lms/embed-pages', { params });
      setEmbedPages(response.data.data || []);
      setTotalPages(response.data.pagination?.total_pages || 1);
    } catch (error) {
      console.error('Error loading embed pages', error);
      showSnackbar('Error loading embed pages', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEmbed = async () => {
    if (!embedForm.name.trim()) {
      showSnackbar('Name is required', 'error');
      return;
    }

    setSaving(true);
    try {
      const params = { chatbot_id: chatbotId };
      const response = await axiosInstance.post('/api/lms/embed-pages', embedForm, { params });
      
      showSnackbar('Embed page created successfully', 'success');
      setCreateDialog(false);
      loadEmbedPages();

      // Open edit dialog with the new embed
      setSelectedEmbed(response.data.data);
      setEmbedForm(transformApiToForm(response.data.data));
      setActiveTab(0);
      setEditDialog(true);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error creating embed page', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEmbed = async () => {
    if (!selectedEmbed) return;

    setSaving(true);
    try {
      await axiosInstance.put(`/api/lms/embed-pages/${selectedEmbed.embed_identity}`, embedForm);
      showSnackbar('Embed page updated successfully', 'success');
      setEditDialog(false);
      loadEmbedPages();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error updating embed page', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (embed, publish) => {
    try {
      await axiosInstance.post(`/api/lms/embed-pages/${embed.embed_identity}/publish`, { publish });
      showSnackbar(`Embed page ${publish ? 'published' : 'unpublished'} successfully`, 'success');
      loadEmbedPages();
    } catch (error) {
      showSnackbar('Error updating status', 'error');
    }
    handleMenuClose();
  };

  const handleDuplicate = async (embed) => {
    try {
      await axiosInstance.post(`/api/lms/embed-pages/${embed.embed_identity}/duplicate`, {
        new_name: `${embed.name} (Copy)`
      });
      showSnackbar('Embed page duplicated successfully', 'success');
      loadEmbedPages();
    } catch (error) {
      showSnackbar('Error duplicating embed page', 'error');
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!deleteDialog.embed) return;

    try {
      await axiosInstance.delete(`/api/lms/embed-pages/${deleteDialog.embed.embed_identity}`);
      showSnackbar('Embed page deleted successfully', 'success');
      setDeleteDialog({ open: false, embed: null });
      loadEmbedPages();
    } catch (error) {
      showSnackbar('Error deleting embed page', 'error');
    }
  };

  const handleCopyCode = (code, type) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(type);
    setTimeout(() => setCopiedCode(''), 2000);
    showSnackbar('Code copied to clipboard', 'success');
  };

  const transformApiToForm = (apiData) => {
    return {
      name: apiData.name || '',
      description: apiData.description || '',
      status: apiData.status || 'draft',
      layout: apiData.layout || getDefaultEmbedForm().layout,
      dimensions: apiData.dimensions || getDefaultEmbedForm().dimensions,
      colors: apiData.colors || getDefaultEmbedForm().colors,
      typography: apiData.typography || getDefaultEmbedForm().typography,
      header: apiData.header || getDefaultEmbedForm().header,
      filters: apiData.filters || getDefaultEmbedForm().filters,
      card_display: apiData.card_display || getDefaultEmbedForm().card_display,
      buttons: apiData.buttons || getDefaultEmbedForm().buttons,
      redirect: apiData.redirect || getDefaultEmbedForm().redirect,
      pagination: apiData.pagination || getDefaultEmbedForm().pagination,
      course_filter: apiData.course_filter,
      max_courses: apiData.max_courses || 12,
      sort_by: apiData.sort_by || 'created_at',
      sort_order: apiData.sort_order || 'desc',
      empty_state: apiData.empty_state || getDefaultEmbedForm().empty_state,
      footer: apiData.footer || getDefaultEmbedForm().footer,
      custom_code: apiData.custom_code || getDefaultEmbedForm().custom_code,
      security: apiData.security || getDefaultEmbedForm().security,
    };
  };

  const handleEditClick = (embed) => {
    setSelectedEmbed(embed);
    setEmbedForm(transformApiToForm(embed));
    setActiveTab(0);
    setEditDialog(true);
    handleMenuClose();
  };

  const handleShowCode = async (embed) => {
    try {
      const response = await axiosInstance.get(`/api/lms/embed-pages/${embed.embed_identity}/code`);
      setCodeDialog({ 
        open: true, 
        embed: { ...embed, embed_code: response.data.data }
      });
    } catch (error) {
      setCodeDialog({ open: true, embed });
    }
    handleMenuClose();
  };

  const handleMenuOpen = (event, embed) => {
    setMenuAnchor(event.currentTarget);
    setSelectedEmbed(embed);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const getStatusChip = (status) => {
    const colors = {
      draft: 'default',
      published: 'success',
      archived: 'error'
    };
    return (
      <Chip
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        color={colors[status] || 'default'}
        size="small"
      />
    );
  };

  const updateFormField = (section, field, value) => {
    setEmbedForm(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  // Render the builder tabs content
  const renderBuilderContent = () => {
    return (
      <>
        <Tabs 
          value={activeTab} 
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab icon={<LayoutIcon />} label="Layout" iconPosition="start" />
          <Tab icon={<PaletteIcon />} label="Colors" iconPosition="start" />
          <Tab icon={<TypographyIcon />} label="Typography" iconPosition="start" />
          <Tab icon={<HeaderIcon />} label="Header" iconPosition="start" />
          <Tab icon={<ImageIcon />} label="Card Display" iconPosition="start" />
          <Tab icon={<ButtonIcon />} label="Buttons" iconPosition="start" />
          <Tab icon={<FilterIcon />} label="Filters" iconPosition="start" />
          <Tab icon={<SecurityIcon />} label="Advanced" iconPosition="start" />
        </Tabs>

        {/* Tab 0: Layout */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Basic Info</Typography>
              <TextField
                fullWidth
                label="Page Name"
                value={embedForm.name}
                onChange={(e) => setEmbedForm({ ...embedForm, name: e.target.value })}
                sx={{ mb: 2 }}
                required
              />
              <TextField
                fullWidth
                label="Description"
                value={embedForm.description}
                onChange={(e) => setEmbedForm({ ...embedForm, description: e.target.value })}
                multiline
                rows={2}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Layout Type</Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Layout</InputLabel>
                <Select
                  value={embedForm.layout?.type || 'grid'}
                  onChange={(e) => updateFormField('layout', 'type', e.target.value)}
                  label="Layout"
                >
                  <MenuItem value="grid">Grid</MenuItem>
                  <MenuItem value="list">List</MenuItem>
                  <MenuItem value="carousel">Carousel</MenuItem>
                </Select>
              </FormControl>
              
              {embedForm.layout?.type === 'grid' && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Columns</InputLabel>
                  <Select
                    value={embedForm.layout?.columns || 3}
                    onChange={(e) => updateFormField('layout', 'columns', e.target.value)}
                    label="Columns"
                  >
                    <MenuItem value={1}>1 Column</MenuItem>
                    <MenuItem value={2}>2 Columns</MenuItem>
                    <MenuItem value={3}>3 Columns</MenuItem>
                    <MenuItem value={4}>4 Columns</MenuItem>
                  </Select>
                </FormControl>
              )}

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Card Style</InputLabel>
                <Select
                  value={embedForm.layout?.card_style || 'standard'}
                  onChange={(e) => updateFormField('layout', 'card_style', e.target.value)}
                  label="Card Style"
                >
                  <MenuItem value="standard">Standard</MenuItem>
                  <MenuItem value="compact">Compact</MenuItem>
                  <MenuItem value="detailed">Detailed</MenuItem>
                  <MenuItem value="minimal">Minimal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>Dimensions</Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Width"
                value={embedForm.dimensions?.width || '100%'}
                onChange={(e) => updateFormField('dimensions', 'width', e.target.value)}
                helperText="e.g., 100%, 800px"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Max Width"
                value={embedForm.dimensions?.max_width || '1200px'}
                onChange={(e) => updateFormField('dimensions', 'max_width', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Min Height"
                value={embedForm.dimensions?.min_height || '400px'}
                onChange={(e) => updateFormField('dimensions', 'min_height', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Padding"
                value={embedForm.dimensions?.padding || '24px'}
                onChange={(e) => updateFormField('dimensions', 'padding', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Border Radius"
                value={embedForm.dimensions?.border_radius || '12px'}
                onChange={(e) => updateFormField('dimensions', 'border_radius', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Card Border Radius"
                value={embedForm.dimensions?.card_border_radius || '8px'}
                onChange={(e) => updateFormField('dimensions', 'card_border_radius', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Gap Between Cards"
                value={embedForm.layout?.gap || '24px'}
                onChange={(e) => updateFormField('layout', 'gap', e.target.value)}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 1: Colors */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Primary Colors</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ColorPicker
                    label="Primary Color"
                    value={embedForm.colors?.primary}
                    onChange={(v) => updateFormField('colors', 'primary', v)}
                  />
                  <ColorPicker
                    label="Secondary Color"
                    value={embedForm.colors?.secondary}
                    onChange={(v) => updateFormField('colors', 'secondary', v)}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Background Colors</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ColorPicker
                    label="Page Background"
                    value={embedForm.colors?.background}
                    onChange={(v) => updateFormField('colors', 'background', v)}
                  />
                  <ColorPicker
                    label="Card Background"
                    value={embedForm.colors?.card_background}
                    onChange={(v) => updateFormField('colors', 'card_background', v)}
                  />
                  <ColorPicker
                    label="Header Background"
                    value={embedForm.colors?.header_background}
                    onChange={(v) => updateFormField('colors', 'header_background', v)}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Text Colors</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ColorPicker
                    label="Text Color"
                    value={embedForm.colors?.text}
                    onChange={(v) => updateFormField('colors', 'text', v)}
                  />
                  <ColorPicker
                    label="Heading Color"
                    value={embedForm.colors?.heading}
                    onChange={(v) => updateFormField('colors', 'heading', v)}
                  />
                  <ColorPicker
                    label="Subtext Color"
                    value={embedForm.colors?.subtext}
                    onChange={(v) => updateFormField('colors', 'subtext', v)}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid item xs={12} md={6}>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Button Colors</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ColorPicker
                    label="Button Color"
                    value={embedForm.colors?.button}
                    onChange={(v) => updateFormField('colors', 'button', v)}
                  />
                  <ColorPicker
                    label="Button Text Color"
                    value={embedForm.colors?.button_text}
                    onChange={(v) => updateFormField('colors', 'button_text', v)}
                  />
                  <ColorPicker
                    label="Button Hover Color"
                    value={embedForm.colors?.button_hover}
                    onChange={(v) => updateFormField('colors', 'button_hover', v)}
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Card Colors</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ColorPicker
                    label="Card Border Color"
                    value={embedForm.colors?.card_border}
                    onChange={(v) => updateFormField('colors', 'card_border', v)}
                  />
                  <TextField
                    fullWidth
                    label="Card Shadow"
                    value={embedForm.colors?.card_shadow}
                    onChange={(e) => updateFormField('colors', 'card_shadow', e.target.value)}
                    sx={{ mb: 2 }}
                    helperText="e.g., 0 2px 8px rgba(0,0,0,0.1)"
                  />
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Difficulty Badge Colors</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <ColorPicker
                    label="Beginner"
                    value={embedForm.colors?.difficulty_beginner}
                    onChange={(v) => updateFormField('colors', 'difficulty_beginner', v)}
                  />
                  <ColorPicker
                    label="Intermediate"
                    value={embedForm.colors?.difficulty_intermediate}
                    onChange={(v) => updateFormField('colors', 'difficulty_intermediate', v)}
                  />
                  <ColorPicker
                    label="Advanced"
                    value={embedForm.colors?.difficulty_advanced}
                    onChange={(v) => updateFormField('colors', 'difficulty_advanced', v)}
                  />
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 2: Typography */}
        <TabPanel value={activeTab} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Font Family"
                value={embedForm.typography?.font_family}
                onChange={(e) => updateFormField('typography', 'font_family', e.target.value)}
                helperText="e.g., 'Inter', sans-serif"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Header Title Size"
                value={embedForm.typography?.header_title_size}
                onChange={(e) => updateFormField('typography', 'header_title_size', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Header Subtitle Size"
                value={embedForm.typography?.header_subtitle_size}
                onChange={(e) => updateFormField('typography', 'header_subtitle_size', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Card Title Size"
                value={embedForm.typography?.card_title_size}
                onChange={(e) => updateFormField('typography', 'card_title_size', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Card Description Size"
                value={embedForm.typography?.card_description_size}
                onChange={(e) => updateFormField('typography', 'card_description_size', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Button Font Size"
                value={embedForm.typography?.button_font_size}
                onChange={(e) => updateFormField('typography', 'button_font_size', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Heading Font Weight</InputLabel>
                <Select
                  value={embedForm.typography?.heading_font_weight || '700'}
                  onChange={(e) => updateFormField('typography', 'heading_font_weight', e.target.value)}
                  label="Heading Font Weight"
                >
                  <MenuItem value="400">Normal (400)</MenuItem>
                  <MenuItem value="500">Medium (500)</MenuItem>
                  <MenuItem value="600">Semi-Bold (600)</MenuItem>
                  <MenuItem value="700">Bold (700)</MenuItem>
                  <MenuItem value="800">Extra Bold (800)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 3: Header */}
        <TabPanel value={activeTab} index={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={embedForm.header?.show !== false}
                    onChange={(e) => updateFormField('header', 'show', e.target.checked)}
                  />
                }
                label="Show Header"
              />
            </Grid>
            {embedForm.header?.show !== false && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Header Title"
                    value={embedForm.header?.title}
                    onChange={(e) => updateFormField('header', 'title', e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Header Subtitle"
                    value={embedForm.header?.subtitle}
                    onChange={(e) => updateFormField('header', 'subtitle', e.target.value)}
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Alignment</InputLabel>
                    <Select
                      value={embedForm.header?.alignment || 'center'}
                      onChange={(e) => updateFormField('header', 'alignment', e.target.value)}
                      label="Alignment"
                    >
                      <MenuItem value="left">Left</MenuItem>
                      <MenuItem value="center">Center</MenuItem>
                      <MenuItem value="right">Right</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={embedForm.header?.show_logo === true}
                        onChange={(e) => updateFormField('header', 'show_logo', e.target.checked)}
                      />
                    }
                    label="Show Logo"
                    sx={{ mb: 2, display: 'block' }}
                  />
                  
                  {embedForm.header?.show_logo && (
                    <>
                      <TextField
                        fullWidth
                        label="Logo URL"
                        value={embedForm.header?.logo_url}
                        onChange={(e) => updateFormField('header', 'logo_url', e.target.value)}
                        sx={{ mb: 2 }}
                      />
                      <TextField
                        fullWidth
                        label="Logo Height"
                        value={embedForm.header?.logo_height}
                        onChange={(e) => updateFormField('header', 'logo_height', e.target.value)}
                      />
                    </>
                  )}
                </Grid>
              </>
            )}
          </Grid>
        </TabPanel>

        {/* Tab 4: Card Display */}
        <TabPanel value={activeTab} index={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Thumbnail Settings</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={embedForm.card_display?.show_thumbnail !== false}
                    onChange={(e) => updateFormField('card_display', 'show_thumbnail', e.target.checked)}
                  />
                }
                label="Show Thumbnail"
              />
              {embedForm.card_display?.show_thumbnail !== false && (
                <>
                  <TextField
                    fullWidth
                    label="Thumbnail Height"
                    value={embedForm.card_display?.thumbnail_height}
                    onChange={(e) => updateFormField('card_display', 'thumbnail_height', e.target.value)}
                    sx={{ mt: 2, mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Default Thumbnail URL"
                    value={embedForm.card_display?.default_thumbnail_url}
                    onChange={(e) => updateFormField('card_display', 'default_thumbnail_url', e.target.value)}
                    helperText="Used when course has no thumbnail"
                  />
                </>
              )}
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Display Options</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={embedForm.card_display?.show_title !== false}
                    onChange={(e) => updateFormField('card_display', 'show_title', e.target.checked)}
                  />
                }
                label="Show Title"
                sx={{ display: 'block' }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={embedForm.card_display?.show_description !== false}
                    onChange={(e) => updateFormField('card_display', 'show_description', e.target.checked)}
                  />
                }
                label="Show Description"
                sx={{ display: 'block' }}
              />
              {embedForm.card_display?.show_description !== false && (
                <FormControl fullWidth sx={{ mt: 1, mb: 1 }}>
                  <InputLabel>Description Lines</InputLabel>
                  <Select
                    value={embedForm.card_display?.description_lines || 2}
                    onChange={(e) => updateFormField('card_display', 'description_lines', e.target.value)}
                    label="Description Lines"
                  >
                    <MenuItem value={1}>1 Line</MenuItem>
                    <MenuItem value={2}>2 Lines</MenuItem>
                    <MenuItem value={3}>3 Lines</MenuItem>
                    <MenuItem value={4}>4 Lines</MenuItem>
                  </Select>
                </FormControl>
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={embedForm.card_display?.show_duration !== false}
                    onChange={(e) => updateFormField('card_display', 'show_duration', e.target.checked)}
                  />
                }
                label="Show Duration"
                sx={{ display: 'block' }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={embedForm.card_display?.show_difficulty !== false}
                    onChange={(e) => updateFormField('card_display', 'show_difficulty', e.target.checked)}
                  />
                }
                label="Show Difficulty"
                sx={{ display: 'block' }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={embedForm.card_display?.show_category !== false}
                    onChange={(e) => updateFormField('card_display', 'show_category', e.target.checked)}
                  />
                }
                label="Show Category"
                sx={{ display: 'block' }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={embedForm.card_display?.show_module_count !== false}
                    onChange={(e) => updateFormField('card_display', 'show_module_count', e.target.checked)}
                  />
                }
                label="Show Module Count"
                sx={{ display: 'block' }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={embedForm.card_display?.show_enrolled_count === true}
                    onChange={(e) => updateFormField('card_display', 'show_enrolled_count', e.target.checked)}
                  />
                }
                label="Show Enrolled Count"
                sx={{ display: 'block' }}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 5: Buttons */}
        <TabPanel value={activeTab} index={5}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Enroll Button</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={embedForm.buttons?.show_enroll !== false}
                    onChange={(e) => updateFormField('buttons', 'show_enroll', e.target.checked)}
                  />
                }
                label="Show Enroll Button"
              />
              {embedForm.buttons?.show_enroll !== false && (
                <>
                  <TextField
                    fullWidth
                    label="Button Text"
                    value={embedForm.buttons?.enroll_text}
                    onChange={(e) => updateFormField('buttons', 'enroll_text', e.target.value)}
                    sx={{ mt: 2, mb: 2 }}
                  />
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Button Style</InputLabel>
                    <Select
                      value={embedForm.buttons?.enroll_style || 'filled'}
                      onChange={(e) => updateFormField('buttons', 'enroll_style', e.target.value)}
                      label="Button Style"
                    >
                      <MenuItem value="filled">Filled</MenuItem>
                      <MenuItem value="outlined">Outlined</MenuItem>
                      <MenuItem value="text">Text</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={embedForm.buttons?.enroll_full_width !== false}
                        onChange={(e) => updateFormField('buttons', 'enroll_full_width', e.target.checked)}
                      />
                    }
                    label="Full Width Button"
                  />
                  <TextField
                    fullWidth
                    label="Button Border Radius"
                    value={embedForm.buttons?.button_border_radius}
                    onChange={(e) => updateFormField('buttons', 'button_border_radius', e.target.value)}
                    sx={{ mt: 2 }}
                  />
                </>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 6: Filters */}
        <TabPanel value={activeTab} index={6}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Search & Filter Options</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={embedForm.filters?.show_search !== false}
                    onChange={(e) => updateFormField('filters', 'show_search', e.target.checked)}
                  />
                }
                label="Show Search"
                sx={{ display: 'block' }}
              />
              {embedForm.filters?.show_search !== false && (
                <TextField
                  fullWidth
                  label="Search Placeholder"
                  value={embedForm.filters?.search_placeholder}
                  onChange={(e) => updateFormField('filters', 'search_placeholder', e.target.value)}
                  sx={{ mt: 1, mb: 2 }}
                />
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={embedForm.filters?.show_category_filter !== false}
                    onChange={(e) => updateFormField('filters', 'show_category_filter', e.target.checked)}
                  />
                }
                label="Show Category Filter"
                sx={{ display: 'block' }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={embedForm.filters?.show_difficulty_filter !== false}
                    onChange={(e) => updateFormField('filters', 'show_difficulty_filter', e.target.checked)}
                  />
                }
                label="Show Difficulty Filter"
                sx={{ display: 'block' }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={embedForm.filters?.show_sort_options !== false}
                    onChange={(e) => updateFormField('filters', 'show_sort_options', e.target.checked)}
                  />
                }
                label="Show Sort Options"
                sx={{ display: 'block' }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Pagination</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={embedForm.pagination?.enabled !== false}
                    onChange={(e) => updateFormField('pagination', 'enabled', e.target.checked)}
                  />
                }
                label="Enable Pagination"
                sx={{ display: 'block' }}
              />
              {embedForm.pagination?.enabled !== false && (
                <>
                  <TextField
                    fullWidth
                    type="number"
                    label="Courses Per Page"
                    value={embedForm.pagination?.per_page}
                    onChange={(e) => updateFormField('pagination', 'per_page', parseInt(e.target.value) || 9)}
                    sx={{ mt: 2, mb: 2 }}
                    inputProps={{ min: 1, max: 50 }}
                  />
                  <FormControl fullWidth>
                    <InputLabel>Pagination Style</InputLabel>
                    <Select
                      value={embedForm.pagination?.style || 'numbered'}
                      onChange={(e) => updateFormField('pagination', 'style', e.target.value)}
                      label="Pagination Style"
                    >
                      <MenuItem value="numbered">Numbered</MenuItem>
                      <MenuItem value="load_more">Load More Button</MenuItem>
                      <MenuItem value="infinite">Infinite Scroll</MenuItem>
                    </Select>
                  </FormControl>
                </>
              )}
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Course Sorting</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>Sort By</InputLabel>
                      <Select
                        value={embedForm.sort_by || 'created_at'}
                        onChange={(e) => setEmbedForm({ ...embedForm, sort_by: e.target.value })}
                        label="Sort By"
                      >
                        <MenuItem value="created_at">Created Date</MenuItem>
                        <MenuItem value="title">Title</MenuItem>
                        <MenuItem value="sort_order">Custom Order</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel>Order</InputLabel>
                      <Select
                        value={embedForm.sort_order || 'desc'}
                        onChange={(e) => setEmbedForm({ ...embedForm, sort_order: e.target.value })}
                        label="Order"
                      >
                        <MenuItem value="asc">Ascending</MenuItem>
                        <MenuItem value="desc">Descending</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
              
              <TextField
                fullWidth
                type="number"
                label="Max Courses (0 = unlimited)"
                value={embedForm.max_courses}
                onChange={(e) => setEmbedForm({ ...embedForm, max_courses: parseInt(e.target.value) || 0 })}
                sx={{ mt: 2 }}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 7: Advanced */}
        <TabPanel value={activeTab} index={7}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Empty State</Typography>
              <TextField
                fullWidth
                label="Empty State Title"
                value={embedForm.empty_state?.title}
                onChange={(e) => updateFormField('empty_state', 'title', e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Empty State Message"
                value={embedForm.empty_state?.message}
                onChange={(e) => updateFormField('empty_state', 'message', e.target.value)}
                multiline
                rows={2}
              />
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Footer</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={embedForm.footer?.show === true}
                      onChange={(e) => updateFormField('footer', 'show', e.target.checked)}
                    />
                  }
                  label="Show Footer"
                />
                {embedForm.footer?.show && (
                  <>
                    <TextField
                      fullWidth
                      label="Footer Text"
                      value={embedForm.footer?.text}
                      onChange={(e) => updateFormField('footer', 'text', e.target.value)}
                      sx={{ mt: 2, mb: 2 }}
                      multiline
                      rows={2}
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={embedForm.footer?.show_powered_by !== false}
                          onChange={(e) => updateFormField('footer', 'show_powered_by', e.target.checked)}
                        />
                      }
                      label="Show 'Powered By'"
                    />
                  </>
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Custom CSS</Typography>
              <TextField
                fullWidth
                label="Custom CSS"
                value={embedForm.custom_code?.css}
                onChange={(e) => updateFormField('custom_code', 'css', e.target.value)}
                multiline
                rows={8}
                placeholder=".embed-container { /* your styles */ }"
                sx={{ fontFamily: 'monospace' }}
              />
              
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Security</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={embedForm.security?.require_domain_verification === true}
                      onChange={(e) => updateFormField('security', 'require_domain_verification', e.target.checked)}
                    />
                  }
                  label="Require Domain Verification"
                />
                <TextField
                  fullWidth
                  label="Allowed Domains"
                  value={(embedForm.security?.allowed_domains || []).join(', ')}
                  onChange={(e) => updateFormField('security', 'allowed_domains', 
                    e.target.value.split(',').map(d => d.trim()).filter(d => d)
                  )}
                  sx={{ mt: 2 }}
                  helperText="Comma-separated list: example.com, www.example.com"
                />
              </Box>
            </Grid>
          </Grid>
        </TabPanel>
      </>
    );
  };

  if (checkingPermissions) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Alert severity="error" sx={{ mt: 6 }}>
        <strong>Access Denied</strong><br />
        You don't have permission to view embed pages.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/lms`)}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700}>
              <CodeIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 32 }} />
              Embed Pages
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create embeddable course catalogs for your website
            </Typography>
          </Box>
        </Box>
        {canModify && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEmbedForm(getDefaultEmbedForm());
              setCreateDialog(true);
            }}
          >
            Create Embed Page
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search embed pages..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadEmbedPages}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Embed Pages Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : embedPages.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <CodeIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No embed pages found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm || statusFilter
              ? 'Try adjusting your filters'
              : 'Create your first embed page to display courses on your website'}
          </Typography>
          {canModify && !searchTerm && !statusFilter && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEmbedForm(getDefaultEmbedForm());
                setCreateDialog(true);
              }}
            >
              Create Embed Page
            </Button>
          )}
        </Paper>
      ) : (
        <>
          <Grid container spacing={3}>
            {embedPages.map((embed) => (
              <Grid item xs={12} sm={6} md={4} key={embed.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: '0.3s',
                    '&:hover': {
                      boxShadow: 6,
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  {/* Preview Header */}
                  <Box
                    sx={{
                      height: 80,
                      background: `linear-gradient(135deg, ${embed.colors?.primary || '#1976d2'} 0%, ${embed.colors?.secondary || '#dc004e'} 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                  >
                    <Typography variant="h6" color="white" fontWeight={600}>
                      {embed.header?.title || 'Course Catalog'}
                    </Typography>
                    <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                      {getStatusChip(embed.status)}
                    </Box>
                  </Box>

                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Tooltip title="Embed Identity" arrow>
                        <Chip 
                          icon={<IdIcon sx={{ fontSize: '14px !important' }} />}
                          label={embed.embed_identity}
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </Tooltip>
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, embed)}>
                        <MoreVertIcon />
                      </IconButton>
                    </Box>

                    <Typography variant="h6" gutterBottom noWrap title={embed.name}>
                      {embed.name}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, height: 40, overflow: 'hidden' }}>
                      {embed.description || 'No description'}
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
                      <Chip
                        icon={<LayoutIcon sx={{ fontSize: 14 }} />}
                        label={embed.layout?.type || 'grid'}
                        size="small"
                        variant="outlined"
                      />
                      {embed.layout?.type === 'grid' && (
                        <Chip
                          label={`${embed.layout?.columns || 3} cols`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>

                    <Divider sx={{ my: 1 }} />

                    {embed.stats && (
                      <Grid container spacing={1}>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">
                            Views
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {embed.stats.view_count || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">
                            Clicks
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {embed.stats.enroll_click_count || 0}
                          </Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" color="text.secondary">
                            Visitors
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {embed.stats.unique_visitors || 0}
                          </Typography>
                        </Grid>
                      </Grid>
                    )}
                  </CardContent>

                  <CardActions sx={{ p: 2, pt: 0, flexWrap: 'wrap', gap: 1 }}>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEditClick(embed)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      startIcon={<CodeIcon />}
                      onClick={() => handleShowCode(embed)}
                    >
                      Get Code
                    </Button>
                    <Button
                      size="small"
                      startIcon={<ViewIcon />}
                      onClick={() => window.open(`/lms/course/embbed/${embed.public_token}`, '_blank')}
                    >
                      Preview
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditClick(selectedEmbed)}>
          <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleShowCode(selectedEmbed)}>
          <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Get Embed Code</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => window.open(`/lms/course/embbed/${selectedEmbed?.public_token}`, '_blank')}>
          <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Preview</ListItemText>
        </MenuItem>
        <Divider />
        {selectedEmbed?.status === 'draft' && (
          <MenuItem onClick={() => handlePublish(selectedEmbed, true)}>
            <ListItemIcon><PublishIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Publish</ListItemText>
          </MenuItem>
        )}
        {selectedEmbed?.status === 'published' && (
          <MenuItem onClick={() => handlePublish(selectedEmbed, false)}>
            <ListItemIcon><PauseIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Unpublish</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={() => handleDuplicate(selectedEmbed)}>
          <ListItemIcon><DuplicateIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Duplicate</ListItemText>
        </MenuItem>
        {canDelete && (
          <>
            <Divider />
            <MenuItem
              onClick={() => {
                handleMenuClose();
                setDeleteDialog({ open: true, embed: selectedEmbed });
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Create Dialog */}
      <Dialog 
        open={createDialog} 
        onClose={() => setCreateDialog(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Create New Embed Page</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Page Name"
            value={embedForm.name}
            onChange={(e) => setEmbedForm({ ...embedForm, name: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
            required
            autoFocus
          />
          <TextField
            fullWidth
            label="Description"
            value={embedForm.description}
            onChange={(e) => setEmbedForm({ ...embedForm, description: e.target.value })}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateEmbed}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Create & Customize'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog 
        open={editDialog} 
        onClose={() => setEditDialog(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{ sx: { height: '90vh' } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Edit Embed Page: {embedForm.name}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={<ViewIcon />}
                onClick={() => window.open(`/lms/course/embbed/${selectedEmbed?.public_token}`, '_blank')}
              >
                Preview
              </Button>
              <Chip 
                label={selectedEmbed?.embed_identity}
                size="small"
                variant="outlined"
              />
            </Stack>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {renderBuilderContent()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleUpdateEmbed}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Code Dialog */}
      <Dialog 
        open={codeDialog.open} 
        onClose={() => setCodeDialog({ open: false, embed: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Embed Code</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Copy and paste one of these code snippets into your website to display your course catalog.
          </Alert>
          
          <Typography variant="subtitle2" gutterBottom>
            Option 1: iFrame Embed (Recommended)
          </Typography>
          <Paper 
            sx={{ 
              p: 2, 
              mb: 3, 
              bgcolor: 'grey.100',
              position: 'relative'
            }}
          >
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: 12 }}>
              {codeDialog.embed?.embed_code?.iframe || `<iframe 
                src="${window.location.origin}/lms/course/embbed/${codeDialog.embed?.public_token}" 
                width="100%" 
                style="min-height: 600px; border: none;" 
                frameborder="0"
            ></iframe>`}
            </pre>
            <IconButton 
              sx={{ position: 'absolute', top: 8, right: 8 }}
              onClick={() => handleCopyCode(
                codeDialog.embed?.embed_code?.iframe || `<iframe src="${window.location.origin}/lms/course/embbed/${codeDialog.embed?.public_token}" width="100%" style="min-height: 600px; border: none;" frameborder="0"></iframe>`,
                'iframe'
              )}
            >
              {copiedCode === 'iframe' ? <CheckIcon color="success" /> : <CopyIcon />}
            </IconButton>
          </Paper>

          <Typography variant="subtitle2" gutterBottom>
            Option 2: Direct Link
          </Typography>
          <Paper 
            sx={{ 
              p: 2, 
              bgcolor: 'grey.100',
              position: 'relative'
            }}
          >
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              {codeDialog.embed?.embed_code?.direct_url || `${window.location.origin}/lms/course/embbed/${codeDialog.embed?.public_token}`}
            </Typography>
            <IconButton 
              sx={{ position: 'absolute', top: 8, right: 8 }}
              onClick={() => handleCopyCode(
                codeDialog.embed?.embed_code?.direct_url || `${window.location.origin}/lms/course/embbed/${codeDialog.embed?.public_token}`,
                'link'
              )}
            >
              {copiedCode === 'link' ? <CheckIcon color="success" /> : <CopyIcon />}
            </IconButton>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCodeDialog({ open: false, embed: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialog.open} 
        onClose={() => setDeleteDialog({ open: false, embed: null })}
      >
        <DialogTitle>Delete Embed Page</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "<strong>{deleteDialog.embed?.name}</strong>"?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. Any websites using this embed code will stop working.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, embed: null })}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LMSEmbedPages;